import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "../components/Toast";
import LoadingSpinner from "../components/LoadingSpinner";
import ConfirmDialog from "../components/ConfirmDialog";
import KnowledgeSourceItem from "../components/KnowledgeSourceItem";
import FileUpload from "../components/KnowledgeUpload/FileUpload";
import UrlUpload from "../components/KnowledgeUpload/UrlUpload";
import TextUpload from "../components/KnowledgeUpload/TextUpload";
import useConfirm from "../hooks/useConfirm";
import useAsyncSet from "../hooks/useAsyncSet";
import { widgetApi, knowledgeApi } from "../services/api";
import errorTracker from "../utils/errorTracker";
import { addCsrfToFormData } from "../utils/csrf";
import { VALIDATION_MESSAGES } from "../utils/constants";
import { sanitizeUrl, getErrorMessage } from "../utils/formatters";
import "./KnowledgeManager.css";

const TAB_TYPES = {
  FILE: "file",
  URL: "url",
  TEXT: "text",
};

const KnowledgeManager = () => {
  const { id: widgetId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirmState, confirm } = useConfirm();

  const [widget, setWidget] = useState(null);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingIds, deletingOps] = useAsyncSet();
  const [reprocessingIds, reprocessingOps] = useAsyncSet();
  const [activeTab, setActiveTab] = useState(TAB_TYPES.FILE);

  const loadData = useCallback(
    async (signal) => {
      setLoading(true);
      try {
        // Load widget first to fail fast if it doesn't exist
        const widgetResponse = await widgetApi.getWidget(widgetId, { signal });
        setWidget(widgetResponse.widget);

        // Then load sources
        const sourcesResponse = await knowledgeApi.getKnowledgeSources(
          widgetId,
          { signal },
        );
        setSources(sourcesResponse.sources || []);
      } catch (err) {
        // Ignore abort errors (component unmounted)
        if (err.name === "AbortError" || err.name === "CanceledError") {
          return;
        }

        errorTracker.captureException(err, {
          context: "loadKnowledgeData",
          widgetId,
        });

        // Provide specific error message based on the error type
        let errorMsg = getErrorMessage(err, "Failed to load data");
        if (err.response?.status === 404) {
          errorMsg = "Widget not found. It may have been deleted.";
        }

        toast.error(errorMsg);
        navigate("/widgets");
      } finally {
        setLoading(false);
      }
    },
    [widgetId, navigate, toast],
  );

  useEffect(() => {
    if (!widgetId) return;

    const controller = new AbortController();
    loadData(controller.signal);

    return () => {
      controller.abort();
    };
  }, [widgetId, loadData]);

  const handleUploadFile = async (file, clearCallback) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);
    formData.append("type", "file");
    addCsrfToFormData(formData);

    setUploading(true);
    try {
      const response = await knowledgeApi.addKnowledgeSource(
        widgetId,
        formData,
      );
      toast.success("File uploaded successfully");
      setSources((prevSources) => [...prevSources, response]);
      clearCallback();
    } catch (err) {
      errorTracker.captureException(err, { context: "uploadFile", widgetId });
      toast.error(getErrorMessage(err, "Failed to upload file"));
      clearCallback();
    } finally {
      setUploading(false);
    }
  };

  const handleAddUrl = async (url, clearCallback) => {
    const trimmedUrl = url.trim();

    if (!trimmedUrl) {
      toast.error(VALIDATION_MESSAGES.URL_REQUIRED);
      return;
    }

    // Validate URL format and protocol
    const sanitizedUrl = sanitizeUrl(trimmedUrl);
    if (!sanitizedUrl) {
      toast.error(VALIDATION_MESSAGES.URL_INVALID);
      return;
    }

    const formData = new FormData();
    formData.append("url", sanitizedUrl);
    formData.append("name", sanitizedUrl);
    formData.append("type", "url");
    addCsrfToFormData(formData);

    setUploading(true);
    try {
      const response = await knowledgeApi.addKnowledgeSource(
        widgetId,
        formData,
      );
      toast.success("URL added successfully");
      setSources((prevSources) => [...prevSources, response]);
      clearCallback();
    } catch (err) {
      errorTracker.captureException(err, { context: "addUrl", widgetId });
      toast.error(getErrorMessage(err, "Failed to add URL"));
    } finally {
      setUploading(false);
    }
  };

  const handleAddText = async (textData, clearCallback) => {
    if (!textData.title.trim() || !textData.content.trim()) {
      toast.error(VALIDATION_MESSAGES.TEXT_REQUIRED);
      return;
    }

    const formData = new FormData();
    formData.append("name", textData.title.trim());
    formData.append("content", textData.content.trim());
    formData.append("type", "text");
    addCsrfToFormData(formData);

    setUploading(true);
    try {
      const response = await knowledgeApi.addKnowledgeSource(
        widgetId,
        formData,
      );
      toast.success("Text snippet added successfully");
      setSources((prevSources) => [...prevSources, response]);
      clearCallback();
    } catch (err) {
      errorTracker.captureException(err, { context: "addText", widgetId });
      toast.error(getErrorMessage(err, "Failed to add text snippet"));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (sourceId) => {
    const confirmed = await confirm({
      title: "Delete Knowledge Source",
      message:
        "Are you sure you want to delete this knowledge source? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (!confirmed) {
      return;
    }

    deletingOps.add(sourceId);

    try {
      await knowledgeApi.deleteKnowledgeSource(widgetId, sourceId);
      toast.success("Knowledge source deleted");
      setSources((prevSources) => prevSources.filter((s) => s.id !== sourceId));
    } catch (err) {
      errorTracker.captureException(err, {
        context: "deleteKnowledge",
        sourceId,
      });
      toast.error(getErrorMessage(err, "Failed to delete knowledge source"));
    } finally {
      deletingOps.remove(sourceId);
    }
  };

  const handleLoadContent = async (sourceId) => {
    try {
      const response = await knowledgeApi.getKnowledgeSource(
        widgetId,
        sourceId,
      );
      setSources((prevSources) =>
        prevSources.map((s) =>
          s.id === sourceId ? { ...s, content: response.content } : s,
        ),
      );
    } catch (err) {
      errorTracker.captureException(err, {
        context: "loadKnowledgeContent",
        sourceId,
      });
      toast.error(getErrorMessage(err, "Failed to load content"));
    }
  };

  const handleUpdate = async (sourceId, data) => {
    try {
      const response = await knowledgeApi.updateKnowledgeSource(
        widgetId,
        sourceId,
        data,
      );
      toast.success("Knowledge source updated");
      setSources((prevSources) =>
        prevSources.map((s) => (s.id === sourceId ? { ...s, ...response } : s)),
      );
    } catch (err) {
      errorTracker.captureException(err, {
        context: "updateKnowledge",
        sourceId,
      });
      toast.error(getErrorMessage(err, "Failed to update knowledge source"));
      throw err;
    }
  };

  const handleReprocess = async (sourceId) => {
    const confirmed = await confirm({
      title: "Reprocess Knowledge Source",
      message:
        "Reprocess this knowledge source? This will re-extract and re-embed the content.",
      confirmText: "Reprocess",
      cancelText: "Cancel",
      variant: "warning",
    });

    if (!confirmed) {
      return;
    }

    reprocessingOps.add(sourceId);

    try {
      await knowledgeApi.reprocessKnowledgeSource(widgetId, sourceId);
      toast.success("Knowledge source queued for reprocessing");

      // Update status in UI
      setSources((prevSources) =>
        prevSources.map((s) =>
          s.id === sourceId ? { ...s, status: "processing" } : s,
        ),
      );
    } catch (err) {
      errorTracker.captureException(err, {
        context: "reprocessKnowledge",
        sourceId,
      });
      toast.error(getErrorMessage(err, "Failed to reprocess knowledge source"));
    } finally {
      reprocessingOps.remove(sourceId);
    }
  };

  if (loading) {
    return (
      <div className="km-container">
        <LoadingSpinner centered text="Loading knowledge sources..." />
      </div>
    );
  }

  return (
    <div className="km-container">
      <div className="km-header">
        <div>
          <h1>Knowledge Base</h1>
          <p className="km-subtitle">
            Manage knowledge sources for{" "}
            <strong>{widget?.display_name || widget?.name}</strong>
          </p>
        </div>
        <button
          className="km-btn km-btn-secondary"
          onClick={() => navigate("/widgets")}
        >
          Back to Widgets
        </button>
      </div>

      {/* Add Knowledge Source */}
      <div className="km-add-section">
        <h2>Add Knowledge Source</h2>

        <div className="km-tabs">
          <button
            className={`km-tab ${activeTab === TAB_TYPES.FILE ? "active" : ""}`}
            onClick={() => setActiveTab(TAB_TYPES.FILE)}
          >
            📄 Upload File
          </button>
          <button
            className={`km-tab ${activeTab === TAB_TYPES.URL ? "active" : ""}`}
            onClick={() => setActiveTab(TAB_TYPES.URL)}
          >
            🔗 Add URL
          </button>
          <button
            className={`km-tab ${activeTab === TAB_TYPES.TEXT ? "active" : ""}`}
            onClick={() => setActiveTab(TAB_TYPES.TEXT)}
          >
            📝 Add Text
          </button>
        </div>

        <div className="km-tab-content">
          {activeTab === TAB_TYPES.FILE && (
            <FileUpload
              uploading={uploading}
              onUploadSuccess={handleUploadFile}
            />
          )}

          {activeTab === TAB_TYPES.URL && (
            <UrlUpload uploading={uploading} onUpload={handleAddUrl} />
          )}

          {activeTab === TAB_TYPES.TEXT && (
            <TextUpload uploading={uploading} onUpload={handleAddText} />
          )}
        </div>
      </div>

      {/* Knowledge Sources List */}
      <div className="km-sources-section">
        <h2>Knowledge Sources ({sources.length})</h2>

        {sources.length === 0 ? (
          <div className="km-empty">
            <p>No knowledge sources yet. Add your first one above!</p>
          </div>
        ) : (
          <div className="km-sources-list">
            {sources.map((source) => (
              <KnowledgeSourceItem
                key={source.id}
                source={source}
                onDelete={handleDelete}
                onReprocess={handleReprocess}
                onUpdate={handleUpdate}
                onLoadContent={handleLoadContent}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog {...confirmState} />
    </div>
  );
};

export default KnowledgeManager;
